'use client';

import { c as _c } from "react/compiler-runtime";
import type { WaitlistSignupEmailPayload } from '@algoacademy/backend/modules/notifications/email-templates';
import { WAITLIST_AUDIENCE_ROLE_LABELS_PL, WAITLIST_AUDIENCE_ROLES } from '@algoacademy/backend/modules/notifications/waitlist/constants';
import { Button } from '@algoacademy/design-system/web/actions/Button';
import { Link } from '@algoacademy/design-system/web/actions/Link';
import { showErrorToastWithAlgoPhoneNumber } from '@algoacademy/design-system/web/feedback/Toaster/ToasterError';
import { Checkbox, CheckboxField } from '@algoacademy/design-system/web/forms/Checkbox';
import { ErrorMessage, Field, Label } from '@algoacademy/design-system/web/forms/Fieldset';
import { Input } from '@algoacademy/design-system/web/forms/Input';
import { Listbox, ListboxLabel, ListboxOption } from '@algoacademy/design-system/web/forms/Listbox';
import { VerticalSpacer } from '@algoacademy/design-system/web/layouts/VerticalSpacer';
import { H3 } from '@algoacademy/design-system/web/marketing/typography/Heading';
import { P3 } from '@algoacademy/design-system/web/marketing/typography/Paragraph';
import { Dialog, DialogBody } from '@algoacademy/design-system/web/overlays/Dialog';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { cn } from '@/app/_lib/utils';
import { useTRPC } from '@/app/_trpc/config';
import { type WaitlistSignupFormData, waitlistSignupDefaultValues, waitlistSignupSchemaZ } from '../_types/waitlistSignupSchema';
import { useWaitlistSignup } from '../WaitlistSignupContext';
function mapFormToEmailPayload(data: WaitlistSignupFormData, productInterest: string | null): WaitlistSignupEmailPayload {
  if (!data.consentWaitlist) {
    throw new Error('Waitlist contact consent is required');
  }
  return {
    email: data.email,
    fullName: data.fullName,
    audienceRole: data.audienceRole,
    phone: data.phone.trim() || null,
    consentWaitlist: data.consentWaitlist,
    consentMarketingOffers: data.consentMarketingOffers,
    productInterest
  };
}
function WaitlistSignupForm(t0) {
  const $ = _c(81);
  const {
    close,
    isCurrentOpening,
    openKey,
    productInterest
  } = t0;
  const trpc = useTRPC();
  let t1;
  if ($[0] !== trpc.notifications.submitWaitlistSignup) {
    t1 = trpc.notifications.submitWaitlistSignup.mutationOptions();
    $[0] = trpc.notifications.submitWaitlistSignup;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const submitWaitlistMutation = useMutation(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      mode: "onSubmit",
      resolver: zodResolver(waitlistSignupSchemaZ),
      defaultValues: waitlistSignupDefaultValues
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const methods = useForm(t2);
  const {
    register,
    control,
    handleSubmit,
    formState: t3
  } = methods;
  const {
    errors,
    isSubmitting
  } = t3;
  let t4;
  if ($[3] !== close || $[4] !== isCurrentOpening || $[5] !== openKey || $[6] !== productInterest || $[7] !== submitWaitlistMutation) {
    t4 = async data => {
      const payload = mapFormToEmailPayload(data, productInterest);
      try {
        await submitWaitlistMutation.mutateAsync(payload);
        if (!isCurrentOpening(openKey)) {
          return;
        }
        toast.success("Zg\u0142oszenie zosta\u0142o przes\u0142ane", {
          description: "Na podany adres e-mail wys\u0142ali\u015Bmy kr\xF3tkie potwierdzenie."
        });
        close();
      } catch {
        if (isCurrentOpening(openKey)) {
          showErrorToastWithAlgoPhoneNumber();
        }
      }
    };
    $[3] = close;
    $[4] = isCurrentOpening;
    $[5] = openKey;
    $[6] = productInterest;
    $[7] = submitWaitlistMutation;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const onSubmit = t4;
  let t5;
  if ($[9] !== handleSubmit || $[10] !== onSubmit) {
    t5 = handleSubmit(onSubmit);
    $[9] = handleSubmit;
    $[10] = onSubmit;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Label>Zgłaszam się jako*</Label>;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== control) {
    t7 = <Controller name="audienceRole" control={control} render={_temp2} />;
    $[13] = control;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const t8 = errors.audienceRole?.message;
  let t9;
  if ($[15] !== t8) {
    t9 = <ErrorMessage>{t8}</ErrorMessage>;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t7 || $[18] !== t9) {
    t10 = <Field>{t6}{t7}{t9}</Field>;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Label>Imię i nazwisko*</Label>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== register) {
    t12 = register("fullName");
    $[21] = register;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12) {
    t13 = <Input {...t12} type="text" autoComplete="name" />;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const t14 = errors.fullName?.message;
  let t15;
  if ($[25] !== t14) {
    t15 = <ErrorMessage>{t14}</ErrorMessage>;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== t13 || $[28] !== t15) {
    t16 = <Field>{t11}{t13}{t15}</Field>;
    $[27] = t13;
    $[28] = t15;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Label>Adres e-mail*</Label>;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== register) {
    t18 = register("email");
    $[31] = register;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t18) {
    t19 = <Input {...t18} type="email" autoComplete="email" />;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const t20 = errors.email?.message;
  let t21;
  if ($[35] !== t20) {
    t21 = <ErrorMessage>{t20}</ErrorMessage>;
    $[35] = t20;
    $[36] = t21;
  } else {
    t21 = $[36];
  }
  let t22;
  if ($[37] !== t19 || $[38] !== t21) {
    t22 = <Field>{t17}{t19}{t21}</Field>;
    $[37] = t19;
    $[38] = t21;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  let t23;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <Label>Telefon (opcjonalnie)</Label>;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== register) {
    t24 = register("phone");
    $[41] = register;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== t24) {
    t25 = <Input {...t24} type="tel" autoComplete="tel" />;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  const t26 = errors.phone?.message;
  let t27;
  if ($[45] !== t26) {
    t27 = <ErrorMessage>{t26}</ErrorMessage>;
    $[45] = t26;
    $[46] = t27;
  } else {
    t27 = $[46];
  }
  let t28;
  if ($[47] !== t25 || $[48] !== t27) {
    t28 = <Field>{t23}{t25}{t27}</Field>;
    $[47] = t25;
    $[48] = t27;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  let t30;
  if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <P3 className="font-medium text-contentPrimary">Zgody prawne</P3>;
    t30 = <VerticalSpacer size="paragraph" />;
    $[50] = t29;
    $[51] = t30;
  } else {
    t29 = $[50];
    t30 = $[51];
  }
  let t31;
  if ($[52] !== control) {
    t31 = <Controller name="consentWaitlist" control={control} render={_temp3} />;
    $[52] = control;
    $[53] = t31;
  } else {
    t31 = $[53];
  }
  const t32 = errors.consentWaitlist?.message;
  let t33;
  if ($[54] !== t32) {
    t33 = <ErrorMessage>{t32}</ErrorMessage>;
    $[54] = t32;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] !== t31 || $[57] !== t33) {
    t34 = <Field>{t31}{t33}</Field>;
    $[56] = t31;
    $[57] = t33;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] !== control) {
    t35 = <Field><Controller name="consentMarketingOffers" control={control} render={_temp4} /></Field>;
    $[59] = control;
    $[60] = t35;
  } else {
    t35 = $[60];
  }
  let t36;
  if ($[61] !== t34 || $[62] !== t35) {
    t36 = <div className="border-borderPrimary border-t pt-4">{t29}{t30}<div className="space-y-3">{t34}{t35}</div></div>;
    $[61] = t34;
    $[62] = t35;
    $[63] = t36;
  } else {
    t36 = $[63];
  }
  let t37;
  if ($[64] !== close) {
    t37 = <Button outline={true} type="button" onClick={close}>Zamknij</Button>;
    $[64] = close;
    $[65] = t37;
  } else {
    t37 = $[65];
  }
  const t38 = isSubmitting || submitWaitlistMutation.isPending;
  let t39;
  if ($[66] !== t38) {
    t39 = <Button type="submit" loading={t38}>Wyślij</Button>;
    $[66] = t38;
    $[67] = t39;
  } else {
    t39 = $[67];
  }
  let t40;
  if ($[68] !== t37 || $[69] !== t39) {
    t40 = <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{t37}{t39}</div>;
    $[68] = t37;
    $[69] = t39;
    $[70] = t40;
  } else {
    t40 = $[70];
  }
  let t41;
  if ($[71] !== t10 || $[72] !== t16 || $[73] !== t22 || $[74] !== t28 || $[75] !== t36 || $[76] !== t40) {
    t41 = <DialogBody className="mt-0 flex min-h-0 min-w-0 flex-1 flex-col space-y-4 overflow-y-auto px-2">{t10}{t16}{t22}{t28}{t36}{t40}</DialogBody>;
    $[71] = t10;
    $[72] = t16;
    $[73] = t22;
    $[74] = t28;
    $[75] = t36;
    $[76] = t40;
    $[77] = t41;
  } else {
    t41 = $[77];
  }
  let t42;
  if ($[78] !== t41 || $[79] !== t5) {
    t42 = <form onSubmit={t5} className="flex min-h-0 min-w-0 flex-1 flex-col pt-4">{t41}</form>;
    $[78] = t41;
    $[79] = t5;
    $[80] = t42;
  } else {
    t42 = $[80];
  }
  return t42;
}
function _temp4(t0) {
  const {
    field: t1
  } = t0;
  const {
    value: value_0,
    onChange: onChange_0
  } = t1;
  return <CheckboxField className="items-start"><Checkbox checked={value_0} onChange={onChange_0} /><Label className="text-contentSecondary text-sm leading-snug lg:text-[0.9375rem] lg:leading-snug">Wyrażam dobrowolną zgodę na otrzymywanie od AlgoAcademy informacji marketingowych drogą elektroniczną, w tym o programach stypendialnych, nowych produktach i usługach oraz aktualnych promocjach. Zgodę mogę w każdej chwili wycofać.</Label></CheckboxField>;
}
function _temp3(t0) {
  const {
    field: t1
  } = t0;
  const {
    value,
    onChange
  } = t1;
  return <CheckboxField className="items-start"><Checkbox checked={value} onChange={onChange} /><Label className="text-sm leading-snug lg:text-[0.9375rem] lg:leading-snug">Wyrażam zgodę na kontakt mailowy w sprawie niniejszego zgłoszenia na listę oczekujących, informacji o terminie startu oraz kolejnych krokach. Podstawy i zakres przetwarzania danych znajdują się w{" "}<Link href="/polityka-prywatnosci" className="font-semibold text-algoprimary" rel="noopener noreferrer" target="_blank">Polityce prywatności</Link>.<span className="text-contentRed">*</span></Label></CheckboxField>;
}
function _temp2(t0) {
  const {
    field
  } = t0;
  return <Listbox value={field.value ?? null} onChange={field.onChange} placeholder="Wybierz z listy" aria-label={"Zg\u0142aszam si\u0119 jako"}>{WAITLIST_AUDIENCE_ROLES.map(_temp)}</Listbox>;
}
function _temp(role) {
  return <ListboxOption key={role} value={role}><ListboxLabel>{WAITLIST_AUDIENCE_ROLE_LABELS_PL[role]}</ListboxLabel></ListboxOption>;
}
export function WaitlistSignupDialog(t0) {
  const $ = _c(17);
  const {
    className
  } = t0;
  const {
    isOpen,
    openKey,
    close,
    isCurrentOpening,
    productInterest
  } = useWaitlistSignup();
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex max-h-[min(92vh,40rem)] max-w-xl flex-col", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <H3 className="min-w-0 flex-1 text-balance pr-2">Zapisz się na listę oczekujących</H3>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <XMarkIcon className="size-5 text-contentSecondary" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== close) {
    t4 = <div className="flex shrink-0 flex-row items-start justify-between gap-4 px-2">{t2}<Button plain={true} onClick={close} aria-label="Zamknij" className="shrink-0">{t3}</Button></div>;
    $[4] = close;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== close || $[7] !== isCurrentOpening || $[8] !== openKey || $[9] !== productInterest) {
    t5 = <WaitlistSignupForm key={openKey} close={close} isCurrentOpening={isCurrentOpening} openKey={openKey} productInterest={productInterest} />;
    $[6] = close;
    $[7] = isCurrentOpening;
    $[8] = openKey;
    $[9] = productInterest;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== close || $[12] !== isOpen || $[13] !== t1 || $[14] !== t4 || $[15] !== t5) {
    t6 = <Dialog open={isOpen} onClose={close} type="default" className={t1}>{t4}{t5}</Dialog>;
    $[11] = close;
    $[12] = isOpen;
    $[13] = t1;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}